import type { ServiceType } from '@mathflat/domain/Student'
import { s3URL } from '@mathflat/shared/@common/utils/s3'

export type SubjectMenuState = 'current' | 'switchable'

/**
 * 항목 우측 배지. 새로 열린 서비스라는 표시(OPEN)이고 구독 여부와 무관하다.
 * 색은 과목 색을 따른다 — 과학이 Green-500 인 것이 과목 색과 같은 값이다.
 */
export type SubjectBadge = { label: string; background: string }

/** teacher 앱이 어느 브랜드로 떠 있는지 — commonRepo.appName 과 같은 값 */
export type TeacherBrand = 'mathflat' | 'schoolflat'

/**
 * 과목 심볼의 표현 방식이 브랜드에 따라 다르다.
 * - image: 서비스별 브랜드 로고를 흰 카드 위에 올린다(매쓰플랫).
 * - tinted: 스쿨플랫 깃발 하나를 과목 색 배경 위에 올린다(스쿨플랫). 과목이 늘어도 색만 추가하면 된다.
 */
export type SubjectSymbol =
  | { kind: 'image'; url: string }
  | { kind: 'tinted'; url: string; background: string }

export type SubjectMenuItem = {
  key: ServiceType
  brandName: string
  /** 브랜드명 아래 붙는 과목 라벨. 스쿨플랫은 브랜드명에 과목이 들어가므로 없다. */
  subjectLabel?: string
  symbol: SubjectSymbol
  state: SubjectMenuState
  badge?: SubjectBadge
}

export type SubjectAvailability = {
  isScienceflatFeatureAllowed: boolean
  isEnglishflatFeatureAllowed: boolean
  brand: TeacherBrand
}

const SYMBOL_URL = {
  MATHFLAT: s3URL.mathflatAssetsBucket.teacher('images/logos/mathflat_symbol_logo.png'),
  SCIENCEFLAT: s3URL.mathflatAssetsBucket.teacher('images/logos/scienceflat_symbol_logo.png'),
  ENGLISHFLAT: s3URL.mathflatAssetsBucket.teacher('images/logos/englishflat_symbol_logo.png'),
} satisfies Record<ServiceType, string>

// 배경 없는 흰 깃발 하나. 배경색은 아래 SCHOOLFLAT_SYMBOL_BACKGROUND 로 입힌다.
const SCHOOLFLAT_FLAG_URL = s3URL.mathflatAssetsBucket.teacher(
  'images/logos/schoolflat_symbol_logo.svg',
)

// 수학 #00ABFF=Sky-400, 과학 #22C55E=Green-500, 영어 #977EFF=Violet-400.
const SCHOOLFLAT_SYMBOL_BACKGROUND = {
  MATHFLAT: 'var(--Colors-Sky-400)',
  SCIENCEFLAT: 'var(--Colors-Green-500)',
  ENGLISHFLAT: 'var(--Colors-Violet-400)',
} satisfies Record<ServiceType, string>

export function getSubjectSymbol(key: ServiceType, brand: TeacherBrand): SubjectSymbol {
  if (brand === 'schoolflat') {
    return {
      kind: 'tinted',
      url: SCHOOLFLAT_FLAG_URL,
      background: SCHOOLFLAT_SYMBOL_BACKGROUND[key],
    }
  }

  return { kind: 'image', url: SYMBOL_URL[key] }
}

/**
 * 메뉴에 쓰는 과목 표기. 매쓰플랫은 각 서비스 브랜드를 그대로 부르고,
 * 스쿨플랫은 자기 브랜드에 과목을 붙여 부른다("스쿨플랫 수학" / "스쿨플랫 과학" / "스쿨플랫 영어").
 */
const SUBJECT_BRAND_NAME = {
  mathflat: {
    MATHFLAT: '매쓰플랫',
    SCIENCEFLAT: '사이언스플랫',
    ENGLISHFLAT: '잉글리시플랫',
  },
  schoolflat: {
    MATHFLAT: '스쿨플랫 수학',
    SCIENCEFLAT: '스쿨플랫 과학',
    ENGLISHFLAT: '스쿨플랫 영어',
  },
} satisfies Record<TeacherBrand, Record<ServiceType, string>>

const SUBJECT_LABEL = {
  MATHFLAT: '수학',
  SCIENCEFLAT: '과학',
  ENGLISHFLAT: '영어',
} satisfies Record<ServiceType, string>

/** teacher 앱의 현재 과목. 브랜드와 무관하게 항상 수학이다. */
export const CURRENT_SUBJECT: ServiceType = 'MATHFLAT'

/** 트리거 쉼 단계에 띄우는 현재 과목 라벨. 스쿨플랫은 메뉴에 과목 라벨이 없어 여기서 직접 참조한다. */
export function getCurrentSubjectLabel(): string {
  return SUBJECT_LABEL[CURRENT_SUBJECT]
}

const OPEN_BADGE = {
  SCIENCEFLAT: { label: 'OPEN', background: 'var(--Colors-Green-500)' },
  ENGLISHFLAT: { label: 'OPEN', background: 'var(--Colors-Violet-400)' },
} satisfies Record<'SCIENCEFLAT' | 'ENGLISHFLAT', SubjectBadge>

function buildMenuItem(
  key: ServiceType,
  brand: TeacherBrand,
  state: SubjectMenuState,
  badge?: SubjectBadge,
): SubjectMenuItem {
  return {
    key,
    brandName: SUBJECT_BRAND_NAME[brand][key],
    subjectLabel: brand === 'mathflat' ? SUBJECT_LABEL[key] : undefined,
    symbol: getSubjectSymbol(key, brand),
    state,
    badge,
  }
}

export type SubjectCarouselItem = {
  key: ServiceType
  subjectLabel: string
  symbol: SubjectSymbol
  color: string
}

// color 는 플립 시 라벨에 입히는 각 과목 색(심볼 배경색과는 별개 값).
const SUBJECT_CAROUSEL: Omit<SubjectCarouselItem, 'symbol'>[] = [
  { key: 'MATHFLAT', subjectLabel: '수학', color: 'var(--Colors-Sky-500)' },
  { key: 'SCIENCEFLAT', subjectLabel: '과학', color: 'var(--Colors-Green-500)' },
  { key: 'ENGLISHFLAT', subjectLabel: '영어', color: 'var(--Colors-Violet-500)' },
]

/**
 * 트리거 캐러셀에서 로고 대신 텍스트를 띄우는 과목인지.
 * 매쓰플랫은 영어만 "OPEN" 텍스트로 대체하고, 스쿨플랫은 다른 과목과 같은 깃발 심볼을 쓴다.
 */
export function usesTextLogo(key: ServiceType, brand: TeacherBrand): boolean {
  return key === 'ENGLISHFLAT' && brand === 'mathflat'
}

/** 현재 서비스 외에 메뉴에 띄울 과목이 하나라도 있는지 — GNB 노출 여부 판정용. */
export function hasSwitchableSubject(availability: SubjectAvailability): boolean {
  return availability.isScienceflatFeatureAllowed || availability.isEnglishflatFeatureAllowed
}

// 트리거 캐러셀 티저는 권한(restriction 미적용) 있는 과목만 순환한다. 수학은 현재 과목이라 항상 포함.
export function getSubjectCarousel(availability: SubjectAvailability): SubjectCarouselItem[] {
  return SUBJECT_CAROUSEL.filter((item) => {
    if (item.key === 'SCIENCEFLAT') return availability.isScienceflatFeatureAllowed
    if (item.key === 'ENGLISHFLAT') return availability.isEnglishflatFeatureAllowed
    return true
  }).map((item) => ({ ...item, symbol: getSubjectSymbol(item.key, availability.brand) }))
}

// teacher 앱은 항상 수학이 현재 과목이라 수학은 무조건 current.
// 과학·영어는 기능 권한이 있을 때 전환 항목으로 노출한다 — 세부 권한과 이용권은 전환 핸들러가 본다.
// `이용 중` 텍스트는 current 전용이라 영어에는 쓰지 않는다. 한 라벨이 "지금 보는 서비스" 와
// "구독함" 두 뜻을 갖게 되기 때문이다.
export function getSubjectMenuItems(availability: SubjectAvailability): SubjectMenuItem[] {
  const items: SubjectMenuItem[] = [buildMenuItem(CURRENT_SUBJECT, availability.brand, 'current')]

  if (availability.isScienceflatFeatureAllowed) {
    items.push(
      buildMenuItem('SCIENCEFLAT', availability.brand, 'switchable', OPEN_BADGE.SCIENCEFLAT),
    )
  }

  if (availability.isEnglishflatFeatureAllowed) {
    items.push(
      buildMenuItem('ENGLISHFLAT', availability.brand, 'switchable', OPEN_BADGE.ENGLISHFLAT),
    )
  }

  return items
}
